import { HeaderRule } from "src/components/GoTable/interface";
import { formatToCurrency } from "./genericFunctions";
import { sorter } from "./sorter";
import stringToNumber from "./stringToNumber";

/*
  get array without duplicates for wt, fc, os.
  @param data
*/
export const removeDuplicatePoItem = (data: any) => {
  return data?.reduce((item: any[], currentItems: any, index: number) => {
    const hasDuplicate = item?.some(
      (lineItem: any) =>
        currentItems?.itemId === lineItem?.itemId &&
        currentItems?.lineNum === lineItem?.lineNum &&
        currentItems?.ordered === lineItem?.ordered &&
        currentItems?.received === lineItem?.received
    );

    if (!hasDuplicate) {
      item.push(currentItems);
      return item;
    } else {
      return item.map((data: any) => {
        if (
          data?.itemId === currentItems?.itemId &&
          data?.lineNum === currentItems?.lineNum &&
          data?.ordered === currentItems?.ordered &&
          data?.received === currentItems?.received
        ) {
          const sumOfExtendedRetail =
            stringToNumber(data.extendedRetail) +
            stringToNumber(currentItems.extendedRetail);
          const extendedRetail = sumOfExtendedRetail
            ? formatToCurrency(sumOfExtendedRetail)
            : "-";
          const qtyShipped =
            stringToNumber(data.qtyShipped) +
            stringToNumber(currentItems.qtyShipped);
          return { ...data, extendedRetail, qtyShipped };
        }
        return data;
      });
    }
  }, []);
};

export const transferItemSearchFilter = (item: any, text: any) => {
  const palletIdWithPrefix = item.palletId ? "pallet #" + item.palletId : "";
  text = text.trim().toLowerCase();

  return (
    item?.itemHash?.toString().includes(text) ||
    item?.description?.toLowerCase().includes(text) ||
    item?.itemId?.toString().includes(text) ||
    item?.upc?.toString().includes(text) ||
    palletIdWithPrefix.toLowerCase().includes(text)
  );
};

export const palletItemSearch = (data: any, text: any) => {
  const getItems = (result: any, item: any) => {
    let hasMatchedPalletItem = false;
    let hasMatchedSubItem = false;

    const itemCopy = { ...item };

    if (item?.palletItems?.length) {
      const matchedPalletItems = item.palletItems.reduce(
        (palletResult: any, palletItem: any) => {
          const palletItemCopy = { ...palletItem };

          if (palletItem?.subItems?.length) {
            const matchedSubItems = palletItem.subItems.filter((subItem: any) =>
              transferItemSearchFilter(subItem, text)
            );

            if (matchedSubItems?.length) {
              hasMatchedSubItem = true;
              palletItemCopy.subItems = matchedSubItems;
            }
          }

          if (transferItemSearchFilter(palletItem, text)) {
            hasMatchedPalletItem = true;
          }

          if (transferItemSearchFilter(palletItem, text) || hasMatchedSubItem) {
            palletResult.push(palletItemCopy);
          }

          return palletResult;
        },
        []
      );

      itemCopy.palletItems = matchedPalletItems;
    }

    if (item?.subItems?.length) {
      const matchedSubItems = item.subItems.filter((subItem: any) =>
        transferItemSearchFilter(subItem, text)
      );

      if (matchedSubItems?.length) {
        hasMatchedSubItem = true;
        itemCopy.subItems = matchedSubItems;
      }
    }

    if (
      transferItemSearchFilter(itemCopy, text) ||
      hasMatchedPalletItem ||
      hasMatchedSubItem
    ) {
      result.push(itemCopy);
    }

    return result;
  };

  return data.reduce(getItems, []);
};

export const createPoItem = (item: any, expandCollapseKeyName?: any) => {
  const { packOrSize, retail, itemId, department, isPalletMod } = item;
  const poItem: any = {
    ...item,
    packOrSize: packOrSize || "-",
    retail: retail || "-",
    itemHash: +itemId,
    deptHash: department,
    isHighlighted: isPalletMod
  };
  if (expandCollapseKeyName && isPalletMod) {
    poItem[expandCollapseKeyName] = false;
  }
  return poItem;
};

// Below function is to arrange the items in the order => pallet main item and subitems
export const buildPalletItemData = (
  itemData: any,
  expandCollapseConfig?: {
    keyName: string;
    showExpandCollapse: boolean;
  }
) => {
  const itemDataGroupedByPallet = itemData?.reduce(
    (total: any, currentValue: any) => {
      total[currentValue.palletId] = total[currentValue.palletId] || [];
      total[currentValue.palletId].push(currentValue);
      return total;
    },
    Object.create(null)
  );
  const expandCollapseKeyName = expandCollapseConfig?.keyName ?? null;
  const itemAccordionData = [] as any;
  let showHeader = true;
  // The PALLET MODULE table header displayed based on the below "hasPalletModule" flag
  let hasPalletModule = false;
  if (itemDataGroupedByPallet) {
    Object?.keys(itemDataGroupedByPallet)?.forEach((keyItem: string) => {
      const groupArray = itemDataGroupedByPallet[keyItem];
      const nonPalletModItems = [] as any;
      const palletModMainItems = [] as any;
      const palletModMainItemIds = [] as string[];
      groupArray?.forEach((item: any) => {
        if (item?.isPalletMod) {
          hasPalletModule = true;
          palletModMainItems.push(item);
          palletModMainItemIds.push(item?.itemId);
        } else {
          nonPalletModItems?.push(item);
        }
      });
      nonPalletModItems?.forEach((item: any) => {
        if (!palletModMainItemIds?.includes(item?.palletModId)) {
          itemAccordionData.push(
            createPoItem(
              {
                ...item,
                showHeader,
                hasPalletModule,
                isMainShipper: false,
                level: item.palletId ? 2 : 1
              },
              expandCollapseKeyName
            )
          );
          showHeader = false;
        }
      });
      palletModMainItems?.forEach((item: any) => {
        const palletItem: any = createPoItem(
          {
            ...item,
            showHeader,
            hasPalletModule,
            isMainShipper: true,
            level: item?.palletId ? 2 : 1
          },
          expandCollapseKeyName
        );

        palletItem.subItems = groupArray
          ?.map((subItem: any) => {
            if (subItem?.palletModId === item?.itemId) {
              return createPoItem(
                {
                  ...subItem,
                  isMainShipper: false,
                  level: item?.palletId ? 3 : 2
                },
                expandCollapseKeyName
              );
            }
            return null;
          })
          .filter(Boolean);

        itemAccordionData.push(palletItem);
      });
      showHeader = true;
      hasPalletModule = false;
      return null;
    });
  }

  const itemAccordionGroupedByPalletId = itemAccordionData.reduce(
    (result: any, item: any) => {
      if (!item?.palletId?.length) {
        result[""] = result[""] || [];
        result[""].push(item);

        return result;
      }

      result[item?.palletId] = result[item?.palletId] || [];
      result[item?.palletId].push(item);

      return result;
    },
    {}
  );

  let itemTableData: any = [];

  Object.keys(itemAccordionGroupedByPalletId).forEach(
    (palletId, index: number) => {
      if (!palletId) {
        itemTableData = [
          ...itemTableData,
          ...itemAccordionGroupedByPalletId[palletId]
        ];
        return;
      }
      const items: {
        palletId: string;
        palletItems: any;
        showHeader: boolean;
        level: number;
        [key: string | number]: boolean | string | number;
      } = {
        palletId,
        palletItems: itemAccordionGroupedByPalletId[palletId],
        showHeader: true,
        level: 1
      };

      if (expandCollapseKeyName && expandCollapseConfig) {
        items[expandCollapseKeyName] = false;
      }
      itemTableData.push(items);
    }
  );

  // Handle Expand Collapse Configuration

  if (expandCollapseKeyName && itemTableData.length > 0) {
    let containsPalletMode = false;
    itemTableData.forEach(
      (
        item: {
          [x: string]: boolean;
          hasPalletModule: boolean;
          palletId: any;
          palletItems: any;
        },
        index: number
      ) => {
        if (
          (item?.hasPalletModule ||
            (item?.palletId && item?.palletItems.length > 0)) &&
          !containsPalletMode
        ) {
          containsPalletMode = true;
        }
      }
    );
    expandCollapseConfig.showExpandCollapse = containsPalletMode;
  }
  return { itemTableData, expandCollapseConfig };
};

export const getUniqueIdsFromSubItems = (subItems: any[]) =>
  subItems.map((subItem: any) => subItem.uniqueId);

export const getUniqueIdsFromPalletItems = (palletItems: any) => {
  const ids = [] as any[];

  palletItems.forEach((palletItem: any) => {
    if (palletItem.uniqueId) {
      ids.push(palletItem.uniqueId);
    }

    if (Array.isArray(palletItem.subItems)) {
      ids.push(...getUniqueIdsFromSubItems(palletItem.subItems));
    }
  });

  return ids;
};

export const getAllUniqueIdsFromTransfer = (data: any) => {
  const ids = [] as any[];

  data.forEach((pallet: any) => {
    if (pallet.uniqueId) {
      ids.push(pallet.uniqueId);
    }

    if (Array.isArray(pallet.subItems)) {
      ids.push(...getUniqueIdsFromSubItems(pallet.subItems));
    }

    if (Array.isArray(pallet.palletItems)) {
      ids.push(...getUniqueIdsFromPalletItems(pallet.palletItems));
    }
  });

  return ids;
};

export const matchPalletItem = (item: any, itemToMatch: any) =>
  +item.itemId === +itemToMatch.itemId &&
  item.palletId === itemToMatch.palletId &&
  item.palletModId === itemToMatch.palletModId;

export const sortPalletData = (data: any, sortParam: any, sortOrder: any) => {
  data.forEach((item: any) => {
    if (Array.isArray(item.palletItems)) {
      item.palletItems.forEach((palletItem: any) => {
        if (Array.isArray(palletItem.subItems)) {
          sorter(palletItem.subItems, sortParam, sortOrder);
        }
      });

      sorter(item.palletItems, sortParam, sortOrder);
    }

    if (Array.isArray(item.subItems)) {
      sorter(item.subItems, sortParam, sortOrder);
    }
  });

  sorter(data, sortParam, sortOrder);
};

export const formatExportPallets = (data: any) => {
  let hasPalletItems = false;
  let hasSubItems = false;
  let hasNormalItems = false;
  const updatedSubItems = (data: any) =>
    data?.map((subItem: any) => {
      subItem.showHeader = false;
      return subItem;
    });
  const updatedPalletItems = (data: any) =>
    data
      ?.map((eachPalletItem: any, eachPalletItemIndex: any) => {
        eachPalletItem.showHeader = eachPalletItemIndex === 0;
        if (eachPalletItem?.subItems) {
          return [eachPalletItem, ...updatedSubItems(eachPalletItem.subItems)];
        }
        return eachPalletItem;
      })
      .flat();
  const updateFlags = (pallet: boolean, subItem: boolean, normal: boolean) => {
    hasPalletItems = pallet;
    hasSubItems = subItem;
    hasNormalItems = normal;
  };
  const checkNormalItemHeader = (index: any) =>
    index === 0 || hasPalletItems || hasSubItems || hasNormalItems;
  return data
    .map((eachData: any, index: any) => {
      if (hasNormalItems && !hasPalletItems && !hasSubItems) {
        updateFlags(false, false, false);
      }
      if (eachData?.palletItems) {
        eachData.palletItems = updatedPalletItems(eachData?.palletItems);
        updateFlags(true, false, false);
        return [...eachData.palletItems];
      } else if (eachData?.subItems) {
        eachData.subItems = updatedSubItems(eachData?.subItems);
        updateFlags(false, true, false);
        eachData.showHeader = true;
        return [eachData, ...eachData.subItems];
      } else {
        eachData.showHeader = checkNormalItemHeader(index);
        updateFlags(false, false, true);
        return eachData;
      }
    })
    .flat();
};

export const isOnOrderStatus = (status: string) =>
  status?.toLowerCase() === "on order";

// Find and modify qtyShipped header column
// to qtyOrdered column for On Order transfer
export const updateQtyShippedColumnForOnOrderTransfer = (
  headerRules: HeaderRule[]
) => {
  const qtyShippedColIdx = headerRules.findIndex(
    (col) => col.id === "qtyShipped"
  );

  if (qtyShippedColIdx < 0) return;

  headerRules[qtyShippedColIdx] = {
    ...headerRules[qtyShippedColIdx],
    id: "ordered",
    title: "qtyOrdered"
  };
};
